// src/components/DrawerAppBar.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { createSelector } from 'reselect';
import { useMobileTheme } from '../contexts/MobileThemeContext';

import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  styled,
  ListItemButton,
  ListItemIcon,
  Divider,
  Collapse,
  createTheme,
  ThemeProvider,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Select,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { setLogOut } from '../store/auth/reducer';
import { projectController, globalController } from '../api/controller';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DnsIcon from '@mui/icons-material/Dns';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { getProjectSlugFromPathname, getStationTypeValue, getStationUuidFromSession, setStationUuidInSession } from '../utils/uuidSession';
import SettingsIcon from '@mui/icons-material/Settings';
import MapIcon from '@mui/icons-material/Map';
import PublicLocaleToggle from './PublicLocaleToggle';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';
import { MAP_PROVIDER_OPTIONS } from '../constants/mapProviders';
import { useProjectMapsEnabled } from '../hooks/useProjectMapsEnabled';
import { useProjectCapabilities } from '../hooks/useProjectCapabilities';
import { applyModuleGatesToCapabilities } from '../project-modules';
import { TABS, canAct } from '../pages/superadmin-shell/superAdminTabs';
import {
  readGroupsMapProvider,
  writeGroupsMapProvider,
  emitGroupsMapProviderChanged,
  GROUPS_MAP_PROVIDER_CHANGED_EVENT,
  readGroupsViewMode,
  writeGroupsViewMode,
  emitGroupsViewModeChanged,
  GROUPS_MAP_VIEW_MODE_CHANGED_EVENT,
} from '../utils/groupsMapState';

/** Роутинг деталки станции: только актуальный сегмент из App (`/details/:id`). Раньше тянули из утилиты-«варианта» — убрано. */
function stationDetailsRouteSegment() {
  return 'details';
}

function normalizedPathStartsWithStationDetails(path) {
  const p = String(path || '');
  return p === '/details' || p.startsWith('/details/');
}

function normalizedPathStartsWithProjectCard(path) {
  const p = String(path || '');
  return p === '/project-card' || p.startsWith('/project-card/');
}

function isStationDetailsNormalizedSegment(segment) {
  return String(segment || '') === stationDetailsRouteSegment();
}

// Создаем унифицированную тему как в SuperAdminPage
const appTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#1a237e',
      light: '#534bae',
      dark: '#000051',
    },
    background: {
      default: '#071124',
      paper: '#162238',
    },
    text: {
      primary: '#ffffff',
      secondary: '#90a4ae',
    },
  },
  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '4px 0',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#1a237e',
            transform: 'scale(1.02)',
          },
          '&.Mui-selected': {
            backgroundColor: '#1a237e',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#000051',
            },
          },
        },
      },
    },
  },
});

// Исправленный селектор
const selectAuthState = createSelector(
  [state => state.authSlice || {}],
  authSlice => ({
    result: authSlice.result || false,
    role: authSlice.role || null,
    userName: authSlice.userName || null,
    visibleGroups: authSlice.visibleGroups || [],
    visibleSets: authSlice.visibleSets || [],
  })
);

const StyledAppBar = styled(AppBar)(({ themecolors, userrole }) => ({
  backgroundColor: (userrole === 'user' && themecolors) ? themecolors.backgroundColor : '#071124',
  borderBottom: (userrole === 'user' && themecolors) ? `1px solid ${themecolors.borderColor}` : '1px solid #1a237e',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  color: (userrole === 'user' && themecolors) ? themecolors.textPrimary : '#ffffff',
}));

const StyledDrawer = styled(Drawer)(({ themecolors, userrole }) => ({
  // Базовые стили - конкретные стили переопределяются в sx
  '& .MuiDrawer-paper': {
    backgroundColor: (userrole === 'user' && themecolors) ? themecolors.backgroundColor : '#071124',
    color: (userrole === 'user' && themecolors) ? themecolors.textPrimary : '#ffffff',
    borderRight: (userrole === 'user' && themecolors) ? `1px solid ${themecolors.borderColor}` : '1px solid #1a237e',
  }
}));

const DrawerAppBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const projectSlug = getProjectSlugFromPathname(location.pathname);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mobileTheme, toggleMobileTheme, themeColors } = useMobileTheme();
  const { lang, setLang, drawer: d, sa } = usePublicUiLocale();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const [currentGroupName, setCurrentGroupName] = useState('');
  
  // Состояния для проектов суперадмина
  const [superAdminProjects, setSuperAdminProjects] = useState([]);
  const [currentProjectSlug, setCurrentProjectSlug] = useState('');
  const [currentSetName, setCurrentSetName] = useState('');
  const [resolvedHeaderStationName, setResolvedHeaderStationName] = useState('');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [groupsMapProvider, setGroupsMapProvider] = useState(() => readGroupsMapProvider());
  const [groupsViewMode, setGroupsViewMode] = useState(() => readGroupsViewMode());
  const { mapsEnabled } = useProjectMapsEnabled(projectSlug);
  const authState = useSelector(selectAuthState);
  const {
    capabilities: adminCapabilities,
    modules: adminModules,
    capabilitiesReady: adminCapsReady,
  } = useProjectCapabilities(authState.role === 'admin' ? projectSlug : '');
  const adminCaps = useMemo(
    () => applyModuleGatesToCapabilities(adminCapabilities, adminModules),
    [adminCapabilities, adminModules],
  );
  const adminCanRead = (tabValue, sub = null) => {
    if (tabValue === TABS.PROJECT.value) return true;
    if (!adminCapsReady) return false;
    return canAct({ capabilities: adminCaps, tabValue, action: 'read', sub });
  };
  const isLightUserTheme = (isMobile || authState.role === 'user') && mobileTheme === 'white';
  const viewModeToggleSx = isLightUserTheme
    ? {
        '& .MuiToggleButton-root': {
          color: '#0f172a',
          borderColor: 'rgba(15, 23, 42, 0.28)',
          backgroundColor: '#ffffff',
          '&:hover:not(.Mui-selected)': { backgroundColor: '#f1f5f9' },
          '&.Mui-selected': {
            color: '#ffffff',
            backgroundColor: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
          },
        },
      }
    : undefined;


  // Обновляем currentTab на основе текущего пути + ?tab=&sub= search params.
  // matchTabByPath: совпадение по pathname (legacy маршруты) ИЛИ по совпадению query.
  useEffect(() => {
    const matchTab = (tabs) => {
      const fullCurrent = `${location.pathname}${location.search || ''}`;
      // 1) exact match (handle legacy admin/stations etc.)
      const exact = tabs.findIndex((tab) => fullCurrent === tab.path);
      if (exact !== -1) return exact;
      // 2) ?tab= (и ?sub=, если он есть в пункте меню).
      //    Pathname у всех админских пунктов один (/admin), поэтому его
      //    нельзя сравнивать раньше query — иначе подсветка всегда на первом.
      try {
        const currentParams = new URLSearchParams(location.search || '');
        const currentTabId = currentParams.get('tab');
        const currentSubId = currentParams.get('sub');
        if (currentTabId) {
          const byQuery = tabs.findIndex((tab) => {
            const [, query] = (tab.path || '').split('?');
            if (!query) return false;
            const qp = new URLSearchParams(query);
            return (
              qp.get('tab') === currentTabId
              && (qp.get('sub') || '') === (currentSubId || '')
            );
          });
          if (byQuery !== -1) return byQuery;
          const byTabOnly = tabs.findIndex((tab) => {
            const [, query] = (tab.path || '').split('?');
            if (!query) return false;
            const qp = new URLSearchParams(query);
            return qp.get('tab') === currentTabId && !qp.get('sub');
          });
          if (byTabOnly !== -1) return byTabOnly;
        }
      } catch (_e) {
        // ignore parse errors
      }
      // 3) prefix on pathname (маршруты без query)
      const byPath = tabs.findIndex((tab) => {
        const pathOnly = (tab.path || '').split('?')[0];
        return pathOnly && location.pathname === pathOnly;
      });
      if (byPath !== -1) return byPath;
      return -1;
    };

    if (authState.role === 'superadmin') {
      const tabs = getSuperAdminTabs();
      const idx = matchTab(tabs);
      if (idx !== -1) {
        setCurrentTab(idx);
      } else if (location.pathname === '/superadmin') {
        setCurrentTab(location.state?.tab || 0);
      }
    } else if (authState.role === 'admin') {
      const tabs = getAdminTabs();
      const idx = matchTab(tabs);
      setCurrentTab(idx === -1 ? false : idx);
    } else if (authState.role === 'user') {
      const tabs = getUserTabs();
      const idx = matchTab(tabs);
      if (idx !== -1) setCurrentTab(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.state, authState.role, userGroups, adminCapsReady, adminCaps]);

  useEffect(() => {
    if (!authState.result) return;
    (async () => {
      try {
        const hasProject = (() => {
          try {
            const parts = window.location.pathname.split('/').filter(Boolean);
            return parts.length >= 2 && parts[0] === 'p';
          } catch { return false; }
        })();
        if (!hasProject) return;
        const groups = await projectController.get('/set-groups');
        setUserGroups(Array.isArray(groups) ? groups : []);
      } catch (e) {
        console.error('Failed to fetch groups:', e);
      }
    })();
  }, [authState.result]);

  useEffect(() => {
    setGroupsMapProvider(readGroupsMapProvider());
    setGroupsViewMode(readGroupsViewMode());
    const onProviderChanged = (event) => {
      const next = String(event?.detail?.providerKey || '').trim();
      if (!next) return;
      setGroupsMapProvider(next);
    };
    const onViewModeChanged = (event) => {
      const next = String(event?.detail?.mode || '').trim();
      if (next === 'cards' || (mapsEnabled && next === 'map')) {
        setGroupsViewMode(next);
      }
    };
    window.addEventListener(GROUPS_MAP_PROVIDER_CHANGED_EVENT, onProviderChanged);
    window.addEventListener(GROUPS_MAP_VIEW_MODE_CHANGED_EVENT, onViewModeChanged);
    return () => {
      window.removeEventListener(GROUPS_MAP_PROVIDER_CHANGED_EVENT, onProviderChanged);
      window.removeEventListener(GROUPS_MAP_VIEW_MODE_CHANGED_EVENT, onViewModeChanged);
    };
  }, [location.pathname, mapsEnabled]);

  useEffect(() => {
    if (!mapsEnabled && groupsViewMode === 'map') {
      setGroupsViewMode('cards');
      writeGroupsViewMode('cards');
      emitGroupsViewModeChanged('cards');
    }
  }, [groupsViewMode, mapsEnabled]);


  // Функция для обновления названий групп и наборов из localStorage
  const updateNamesFromLocalStorage = () => {
    const normalizedPath = (location.pathname || '').replace(/^\/p\/[^/]+/, '');
    if (authState.role === 'user') {
      // Проверяем, находимся ли мы на странице набора
      if (normalizedPath === '/sets') {
        const selectedSet = JSON.parse(localStorage.getItem('selectedSet') || '{}');
        if (selectedSet && selectedSet.name) {
          setCurrentSetName(selectedSet.name);
          setCurrentGroupName('');
        } else {
          // Если нет сохраненного набора, очищаем название
          setCurrentSetName('');
          setCurrentGroupName('');
        }
      } else if (normalizedPath.startsWith('/groups')) {
        // Находимся на странице группы
        const selectedGroup = JSON.parse(localStorage.getItem('selectedGroup') || '{}');
        if (selectedGroup && selectedGroup.name) {
          setCurrentGroupName(selectedGroup.name);
          setCurrentSetName('');
        } else {
          // Если нет сохраненной группы, очищаем название
          setCurrentGroupName('');
          setCurrentSetName('');
        }
      } else if (normalizedPath.startsWith('/trends/')) {
        // Находимся на странице трендов
        const stationData = JSON.parse(localStorage.getItem('currentStationData') || '{}');
        if (stationData && stationData.Station_name) {
          // Получаем uidConfigs из глобального кеша
          const uidConfigs = typeof window !== 'undefined' ? (window.__UID_CONFIGS__ || {}) : {};
          const uid = stationData.uid || stationData.uid_code;
          const uidName = uid ? (uidConfigs[uid]?.name || uid) : null;
          const stationDisplayName = uidName 
            ? `${stationData.Station_name} (${uidName})`
            : stationData.Station_name;
          setCurrentSetName(d.trendChart(stationDisplayName));
          setCurrentGroupName('');
        } else {
          setCurrentSetName(d.trendChartShort);
          setCurrentGroupName('');
        }
      } else if (
        normalizedPath.startsWith('/alarms/') ||
        normalizedPath.startsWith('/alarms-v2/')
      ) {
        // Находимся на странице аварий - показываем имя текущей станции
        const stationData = JSON.parse(localStorage.getItem('currentStationData') || '{}');
        const stationName =
          stationData?.Station_name ||
          stationData?.name ||
          stationData?.station_name ||
          '';
        setCurrentSetName(stationName || '');
        setCurrentGroupName('');
      } else if (normalizedPathStartsWithStationDetails(normalizedPath)) {
        // Находимся на странице деталей станции - показываем имя станции
        const stationData = JSON.parse(localStorage.getItem('currentStationData') || '{}');
        const stationName = stationData?.Station_name || stationData?.name || '';
        setCurrentSetName(stationName || '');
        setCurrentGroupName('');
      } else {
        // На других страницах показываем группу по умолчанию
        const selectedGroup = JSON.parse(localStorage.getItem('selectedGroup') || '{}');
        if (selectedGroup && selectedGroup.name) {
          setCurrentGroupName(selectedGroup.name);
          setCurrentSetName('');
        } else {
          // Если нет сохраненной группы, очищаем название
          setCurrentGroupName('');
          setCurrentSetName('');
        }
      }
    } else {
      // Для не-пользователей очищаем названия
      setCurrentGroupName('');
      setCurrentSetName('');
    }
  };

  // Определяем текущую группу и набор на основе URL и localStorage
  // Добавляем принудительное обновление при каждом изменении location
  useEffect(() => {
    updateNamesFromLocalStorage();
  }, [location.pathname, location.key, authState.role]); // Добавляем location.key для принудительного обновления

  // Слушатель для изменений localStorage (когда пользователь выбирает группу)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedGroup' || e.key === 'selectedSet' || e.key === 'currentStationData') {
        updateNamesFromLocalStorage();
      }
    };
    const handleStationDataUpdate = () => {
      updateNamesFromLocalStorage();
    };

    // Добавляем слушатель событий storage
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('currentStationDataUpdated', handleStationDataUpdate);

    // Также добавляем слушатель для кликов по документу для принудительного обновления
    const handleDocumentClick = () => {
      // Небольшая задержка, чтобы localStorage успел обновиться
      setTimeout(() => {
        updateNamesFromLocalStorage();
      }, 100);
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('currentStationDataUpdated', handleStationDataUpdate);
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [authState.role, location.pathname]);

  // Функция для получения projectSlug из URL
  const getProjectSlugFromLocation = () => {
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : '/';
      const parts = path.split('/').filter(Boolean);
      return parts.length >= 2 && parts[0] === 'p' ? parts[1] : '';
    } catch { return ''; }
  };

  // База проектного пути и нормализация пути (без префикса /p/:slug)
  const getProjectBase = () => {
    const slug = getProjectSlugFromLocation();
    return slug ? `/p/${slug}` : '';
  };
  const getNormalizedPath = () => {
    try {
      return (location.pathname || '/').replace(/^\/p\/[^/]+/, '');
    } catch { return location.pathname || '/'; }
  };

  const isGenericStationTitle = (name) => {
    if (typeof name !== 'string') return false;
    const s = name.trim();
    return (
      /^станція\s+\d+$/i.test(s) ||
      /^станция\s+\d+$/i.test(s) ||
      /^station\s+\d+$/i.test(s)
    );
  };

  useEffect(() => {
    if (authState.role !== 'user') {
      setResolvedHeaderStationName('');
      return;
    }

    const normalized = getNormalizedPath();
    const isStationPage =
      normalizedPathStartsWithStationDetails(normalized) ||
      normalized.startsWith('/trends/') ||
      normalized.startsWith('/alarms/') ||
      normalized.startsWith('/alarms-v2/');
    if (!isStationPage) {
      setResolvedHeaderStationName('');
      return;
    }

    const stationId = normalized.split('/')[2];
    if (!stationId) {
      setResolvedHeaderStationName('');
      return;
    }

    const params = new URLSearchParams(location.search || '');
    if (params.get('projectOnly') === '1' || params.get('projectOnly') === 'true') {
      setResolvedHeaderStationName('');
      return;
    }

    const queryUid = params.get('uid') || '';
    let cancelled = false;
    const normalizeUuid = (uuid) => String(uuid || '').replace(/-/g, '').toLowerCase();
    const sameUid = (rowUid) =>
      !queryUid || normalizeUuid(rowUid) === normalizeUuid(queryUid);

    try {
      const cached = JSON.parse(localStorage.getItem('currentStationData') || '{}');
      const cachedName = cached?.Station_name || cached?.name || cached?.station_name || '';
      const cachedNum = Number(cached?.Station_num ?? cached?.stationNum ?? cached?.num);
      const cachedUid = cached?.uid || cached?.uid_code || cached?.uuid || '';
      if (
        cachedName &&
        !isGenericStationTitle(cachedName) &&
        Number(cachedNum) === Number(stationId) &&
        sameUid(cachedUid)
      ) {
        setResolvedHeaderStationName(cachedName);
        return;
      }
    } catch {
      // ignore bad cache
    }

    (async () => {
      try {
        const payload = await projectController.get('/data');
        const rows = Array.isArray(payload) ? payload : payload?.items || [];
        const found = rows.find((row) => {
          const rowNum = Number(row?.Station_num ?? row?.stationNum ?? row?.num ?? row?.Station_id);
          if (rowNum !== Number(stationId)) return false;
          const rowUid = row?.uid || row?.uid_code || row?.uuid || '';
          return sameUid(rowUid);
        });
        if (cancelled) return;
        const stationName =
          found?.Station_name ||
          found?.name ||
          found?.station_name ||
          '';
        if (stationName) {
          setResolvedHeaderStationName(stationName);
          const current = JSON.parse(localStorage.getItem('currentStationData') || '{}');
          localStorage.setItem(
            'currentStationData',
            JSON.stringify({
              ...current,
              ...found,
              Station_name: stationName,
              name: found?.name || stationName,
            }),
          );
          window.dispatchEvent(new Event('currentStationDataUpdated'));
        } else {
          setResolvedHeaderStationName('');
        }
      } catch {
        if (!cancelled) setResolvedHeaderStationName('');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authState.role, location.pathname, location.search, projectSlug]);

  const getReturnTargetFromQuery = () => {
    try {
      const sp = new URLSearchParams(location.search || '');
      const target = String(sp.get('returnTo') || '').trim();
      if (!target.startsWith('/p/')) return '';
      if (!/\/project-card(-v2)?\//.test(target)) return '';
      return target;
    } catch {
      return '';
    }
  };
  const getProjectCardTitleFromPath = () => {
    try {
      const normalized = getNormalizedPath();
      if (!normalizedPathStartsWithProjectCard(normalized)) return '';
      const encoded = normalized.split('/')[2] || '';
      if (!encoded) return '';
      return decodeURIComponent(encoded).trim();
    } catch {
      return '';
    }
  };

  const resolveStationUuid = (stationId, stationData) => {
    const stationType = getStationTypeValue(stationData);
    return stationData?.uid || stationData?.uid_code || stationData?.uuid ||
      getStationUuidFromSession(projectSlug, Number(stationId), stationType);
  };

  const persistStationUuid = (stationId, stationData) => {
    const uuid = resolveStationUuid(stationId, stationData);
    setStationUuidInSession(projectSlug, Number(stationId), getStationTypeValue(stationData), uuid);
    return uuid;
  };

  const handleBackFromAlarmOrTrend = (stationId) => {
    const base = getProjectBase();
    const returnTarget = getReturnTargetFromQuery();
    if (returnTarget) {
      navigate(returnTarget);
      setTimeout(() => window.scrollTo(0, 0), 100);
      return;
    }

    if (stationId) {
      const stationData = JSON.parse(localStorage.getItem('currentStationData') || '{}');
      persistStationUuid(stationId, stationData);
      const detSeg = stationDetailsRouteSegment();
      if (stationData && Object.keys(stationData).length > 0) {
        navigate(`${base}/${detSeg}/${stationId}`, { state: { sensorItemProps: stationData } });
      } else {
        navigate(`${base}/${detSeg}/${stationId}`);
      }
      setTimeout(() => window.scrollTo(0, 0), 100);
      return;
    }

    navigate(`${base}/groups`);
  };

  // Функция загрузки проектов суперадмина
  const loadSuperAdminProjects = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
      const superAdminId = Number(storedUser?.id || 0);
      
      
      if (!superAdminId) {
        return;
      }
      
      // Используем новый эндпоинт для получения проектов суперадмина
      let myProjects = [];
      try {
        const response = await globalController.get('/catalog/projects/my');
        myProjects = response?.data?.items || [];
        // Fallback: если пусто, пробуем список по владельцу
        if (!Array.isArray(myProjects) || myProjects.length === 0) {
          const listResp = await globalController.post('/catalog/projects/list');
          const allProjects = listResp?.data?.items || [];
          myProjects = allProjects.filter(p => Number(p.owner_super_admin_id) === superAdminId);
        }
      } catch (error) {
        console.error('Failed to load projects via new API, falling back to old method:', error);
        // Fallback к старому методу
        const response = await globalController.post('/catalog/projects/list');
        const allProjects = response?.data?.items || [];
        myProjects = allProjects.filter(p => Number(p.owner_super_admin_id) === superAdminId);
      }
      
      setSuperAdminProjects(myProjects);
      
      // Устанавливаем текущий проект из URL
      const currentSlug = getProjectSlugFromLocation();
      if (currentSlug) {
        setCurrentProjectSlug(currentSlug);
      }
      
    } catch (error) {
      console.error('Failed to load superadmin projects:', error);
    }
  };

  // Обработчик смены проекта
  const handleProjectChange = (event) => {
    const newProjectSlug = event.target.value;
    
    // Сохраняем выбор
    localStorage.setItem('superadmin_last_project', newProjectSlug);
    
    // Меняем URL (slug)
    navigate(`/p/${newProjectSlug}/superadmin`);
  };

  // Загружаем проекты суперадмина при изменении роли
  useEffect(() => {
    if (authState.role === 'superadmin') {
      loadSuperAdminProjects();
    }
  }, [authState.role]);

  // Синхронизируем currentProjectSlug с URL при изменении location
  useEffect(() => {
    if (authState.role === 'superadmin') {
      const currentSlug = getProjectSlugFromLocation();
      if (currentSlug && currentSlug !== currentProjectSlug) {
        setCurrentProjectSlug(currentSlug);
      }
    }
  }, [location.pathname, authState.role, currentProjectSlug]);



  useEffect(() => {
    const pathNoProjectPrefix = (location.pathname || '').replace(/^\/p\/[^/]+/, '');
    if (
      normalizedPathStartsWithStationDetails(pathNoProjectPrefix) ||
      pathNoProjectPrefix.startsWith('/trends/') ||
      pathNoProjectPrefix.startsWith('/alarms/') ||
      pathNoProjectPrefix.startsWith('/alarms-v2/')
    ) {
      const stateData = location.state?.sensorItemProps;
      if (stateData) {
        // Сохраняем данные станции в localStorage для последующего использования
        localStorage.setItem('currentStationData', JSON.stringify(stateData));
      }
    }
  }, [location.pathname, location.state]);

  // Удалены отдельные функции загрузки групп; логика объединена в единый эффект ниже

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // eslint-disable-next-line no-unused-vars
  const handleLogout = async () => {
    try {
      dispatch(setLogOut());
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Табы суперадмина теперь маппятся на канонический shell URL
  // /p/:slug/superadmin?tab=<slug>. См. pages/superadmin-shell/superAdminTabs.js.
  const getSuperAdminTabs = () => {
    const parts = (location?.pathname || '/').split('/').filter(Boolean);
    const slug = parts[0] === 'p' && parts[1] ? parts[1] : null;
    const base = slug ? `/p/${slug}/superadmin` : '/v2/superadmin';
    return [
      { label: d.tabOverview, value: 0, path: `${base}?tab=project` },
      { label: d.tabSets, value: 1, path: `${base}?tab=layout&sub=sets` },
      { label: d.tabGroups, value: 2, path: `${base}?tab=layout&sub=groups` },
      { label: d.tabUsers, value: 3, path: `${base}?tab=users` },
      { label: d.tabAdmins, value: 4, path: `${base}?tab=users` },
    ];
  };

  // Функция для получения табов пользователя
  const getUserTabs = () => [
    { label: d.myGroupsTab, value: 0, hasDropdown: true },
  ];

  // Табы admin маппятся на единую project-scoped панель /p/:slug/admin?tab=...
  // Старые /admin/users, /admin/sets и т.п. остаются как legacy (deprecated).
  const getAdminTabs = () => {
    const parts = (location?.pathname || '/').split('/').filter(Boolean);
    const slug = parts[0] === 'p' && parts[1] ? parts[1] : null;
    const base = slug ? `/p/${slug}` : '';
    const tabs = [
      { label: sa.tabProject, path: `${base}/admin?tab=project` },
    ];
    if (adminCanRead(TABS.DEVICE.value)) {
      tabs.push({ label: sa.tabDevice, path: `${base}/admin?tab=device` });
    }
    if (adminCanRead(TABS.LOCAL.value)) {
      tabs.push({ label: sa.tabLocal, path: `${base}/admin?tab=local` });
    }
    if (adminCanRead(TABS.LAYOUT.value)) {
      tabs.push({ label: sa.tabLayout, path: `${base}/admin?tab=layout` });
    }
    if (adminCanRead(TABS.USERS.value)) {
      tabs.push({ label: sa.tabUsers, path: `${base}/admin?tab=users` });
    }
    if (adminCanRead(TABS.TELEGRAM.value)) {
      tabs.push({ label: sa.tabTelegram, path: `${base}/admin?tab=telegram` });
    }
    return tabs.map((tab, index) => ({ ...tab, value: index }));
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (authState.role === 'superadmin') {
      const tabs = getSuperAdminTabs();
      const tab = tabs[newValue];
      if (tab && tab.path) navigate(tab.path);
    } else if (authState.role === 'admin') {
      const tabs = getAdminTabs();
      const tab = tabs[newValue];
      if (tab && tab.path) navigate(tab.path);
    } else if (authState.role === 'user') {
      const tabs = getUserTabs();
      const tab = tabs[newValue];
      if (tab && tab.path) navigate(tab.path);
    }
  };

  // Обработчики для выпадающего меню групп
  const handleGroupMenuOpen = (event) => {
    setGroupMenuAnchor(event.currentTarget);
  };

  const handleGroupMenuClose = () => {
    setGroupMenuAnchor(null);
  };

  const handleGroupSelect = (groupId) => {
    // Находим выбранную группу и сохраняем её в localStorage
    const selectedGroup = userGroups.find(group => group.id === groupId);
    if (selectedGroup) {
      localStorage.setItem('selectedGroup', JSON.stringify(selectedGroup));
      // Принудительно обновляем название
      setCurrentGroupName(selectedGroup.name);
      setCurrentSetName('');
    }
    // Навигация в пределах текущего проекта
    const base = getProjectBase();
    navigate(`${base}/groups/${groupId}`);
    handleGroupMenuClose();
    // Закрываем мобильное меню
    setMobileOpen(false);
  };

  const handleBackFromMoreDetails = () => {
    const base = getProjectBase();
    const fromGroupId = Number(
      location.state?.fromGroupId
      ?? sessionStorage.getItem('md:return:fromGroupId')
      ?? '',
    );
    const fromGroupsViewMode = String(
      location.state?.fromGroupsViewMode
      ?? sessionStorage.getItem('md:return:groupsViewMode')
      ?? '',
    ).toLowerCase();
    const fromGroupsMapProvider = String(
      location.state?.fromGroupsMapProvider
      ?? sessionStorage.getItem('md:return:groupsMapProvider')
      ?? '',
    ).trim();
    const selectedGroup = JSON.parse(localStorage.getItem('selectedGroup') || '{}');
    localStorage.removeItem('currentStationData');
    setCurrentSetName('');
    if (Number.isFinite(fromGroupId) && fromGroupId > 0) {
      navigate(`${base}/groups/${fromGroupId}`, {
        state: {
          fromGroupsViewMode: fromGroupsViewMode === 'map' ? 'map' : undefined,
          fromGroupsMapProvider: fromGroupsMapProvider || undefined,
        },
      });
      return;
    }
    if (selectedGroup && selectedGroup.id) {
      navigate(`${base}/groups/${selectedGroup.id}`, {
        state: {
          fromGroupsViewMode: fromGroupsViewMode === 'map' ? 'map' : undefined,
          fromGroupsMapProvider: fromGroupsMapProvider || undefined,
        },
      });
      return;
    }
    navigate(`${base}/groups`, {
      state: {
        fromGroupsViewMode: fromGroupsViewMode === 'map' ? 'map' : undefined,
        fromGroupsMapProvider: fromGroupsMapProvider || undefined,
      },
    });
  };

  // Обработчики для пользовательского меню
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Контекстные действия для текущей страницы
  const handleCharts = () => {
    const base = getProjectBase();
    const normalized = getNormalizedPath().split('/').filter(Boolean);
    if (
      (isStationDetailsNormalizedSegment(normalized[0]) ||
        normalized[0] === 'alarms' ||
        normalized[0] === 'alarms-v2') &&
      normalized[1]
    ) {
      const stationId = normalized[1];
      try {
        const stationData = JSON.parse(localStorage.getItem('currentStationData') || '{}');
        persistStationUuid(stationId, stationData);
        const params = new URLSearchParams(location.search || '');
        const resolvedUuid = resolveStationUuid(stationId, stationData);
        if (resolvedUuid) params.set('uid', resolvedUuid);
        params.set('station', String(stationId));
        const returnTo = `${location.pathname}${location.search || ''}`;
        if (returnTo.startsWith('/p/')) params.set('returnTo', returnTo);
        const qs = params.toString();
        navigate(`${base}/trends/${stationId}${qs ? `?${qs}` : ''}`);
      } catch (e) {
        // Если не удалось получить UUID, навигируем без него
        navigate(`${base}/trends/${stationId}`);
      }
    }
    handleUserMenuClose();
  };

  const handleAlarms = () => {
    const base = getProjectBase();
    const normalized = getNormalizedPath().split('/').filter(Boolean);
    if (
      (isStationDetailsNormalizedSegment(normalized[0]) || normalized[0] === 'trends')
      && normalized[1]
    ) {
      const stationId = normalized[1];
      try {
        const stationData = JSON.parse(localStorage.getItem('currentStationData') || '{}');
        persistStationUuid(stationId, stationData);
        const params = new URLSearchParams(location.search || '');
        const resolvedUuid = resolveStationUuid(stationId, stationData);
        if (resolvedUuid) params.set('uid', resolvedUuid);
        params.set('station', String(stationId));
        const returnTo = `${location.pathname}${location.search || ''}`;
        if (returnTo.startsWith('/p/')) params.set('returnTo', returnTo);
        const qs = params.toString();
        navigate(`${base}/alarms-v2/${stationId}${qs ? `?${qs}` : ''}`);
      } catch (e) {
        // Если не удалось получить UUID, навигируем без него
        navigate(`${base}/alarms-v2/${stationId}`);
      }
    }
    handleUserMenuClose();
  };

  const handleRefreshPage = () => {
    window.location.reload();
    handleUserMenuClose();
  };

  const handleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
    handleUserMenuClose();
  };

  // Функция для получения контекстного меню в зависимости от страницы
  const getContextMenuItems = () => {
    const items = [];
    const base = getProjectBase();
    const hasProjectContext = Boolean(base);

    const normalized = getNormalizedPath();
    if (normalizedPathStartsWithStationDetails(normalized)) {
      // Страница деталей станции
      items.push(
        { 
          icon: <TrendingUpIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.openCharts,
          action: handleCharts 
        },
        { 
          icon: <WarningIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.alarmHistory,
          action: handleAlarms 
        }
      );
    } else if (normalized.startsWith('/trends/')) {
      // Страница графиков
      items.push(
        { 
          icon: <WarningIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.alarmHistory,
          action: handleAlarms 
        },
        { 
          icon: <RefreshIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.refreshData,
          action: handleRefreshPage 
        }
      );
    } else if (
      normalized.startsWith('/alarms/') ||
      normalized.startsWith('/alarms-v2/')
    ) {
      // Страница аварий
      items.push(
        { 
          icon: <TrendingUpIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.openCharts,
          action: handleCharts 
        },
        { 
          icon: <RefreshIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.refreshData,
          action: handleRefreshPage 
        }
      );
    } else if (normalized.startsWith('/groups/') || normalized === '/sets') {
      // Страницы групп и наборов
      items.push(
        { 
          icon: <RefreshIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.refreshData,
          action: handleRefreshPage 
        },
        { 
          icon: <FullscreenIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.fullscreen,
          action: handleFullscreen 
        }
      );
    } else if (normalized.includes('/admin/') || normalized === '/superadmin') {
      // Админские страницы
      items.push(
        { 
          icon: <HomeIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.home,
          action: () => { const base = getProjectBase(); navigate(`${base}/groups`); handleUserMenuClose(); }
        },
        { 
          icon: <RefreshIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.refreshData,
          action: handleRefreshPage 
        }
      );
    } else {
      // Остальные страницы - базовые действия
      items.push(
        { 
          icon: <RefreshIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.refresh,
          action: handleRefreshPage 
        }
      );
    }

    if (hasProjectContext && showSvgRuntime) {
      items.push(
        {
          icon: <DnsIcon sx={{ mr: 2, fontSize: '20px' }} />,
          label: d.svgRuntime,
          action: () => { navigate(`${base}/svg-runtime`); handleUserMenuClose(); }
        }
      );
    }

    // Убираем дублирующий переключатель темы - он уже есть в drawer
    // Добавляем кнопку "Домой" для пользователей (только для ПК, на мобильных она есть в drawer)
    if (authState.role === 'user' && !isMobile) {
      items.push(
        { 
          icon: <HomeIcon sx={{ mr: 2, fontSize: '20px' }} />, 
          label: d.home,
          action: () => { const base = getProjectBase(); navigate(`${base}/groups`); handleUserMenuClose(); }
        }
      );
    }

    return items;
  };

  const showSvgRuntime =
    String(process.env.REACT_APP_SHOW_SVG_RUNTIME || '').toLowerCase() === 'true' ||
    process.env.NODE_ENV !== 'production';

  const handleLogoutFromMenu = async () => {
    try {
      // Определяем slug проекта, если находимся в проектном контексте
      const parts = (location?.pathname || '/').split('/').filter(Boolean);
      const slug = parts[0] === 'p' && parts[1] ? parts[1] : null;
      const isStandalone =
        (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
        (typeof window !== 'undefined' && window.navigator?.standalone === true);
      dispatch(setLogOut());
      if (isMobile && isStandalone) {
        navigate('/');
      } else if (slug) {
        navigate(`/p/${slug}/login`);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleUserMenuClose();
  };

  // handleGoToDefaultGroup удален как неиспользуемый

  const getMenuItems = () => {
    if (!authState.result) {
      return [];
    }

    // Для суперадмина и админа используем табы, а не обычные меню items
    if (authState.role === 'superadmin' || authState.role === 'admin') {
      return [];
    }

    // Только для пользователей возвращаем обычные пункты меню
    const base = getProjectBase();
    const items = [
      {
        text: d.menuHome,
        icon: <HomeIcon />,
        path: `${base}/groups`
      }
    ];

    if (authState.role === 'user') {
      items.push({
        text: d.menuGroups,
        icon: <GroupIcon />,
        path: isMobile ? undefined : `${base}/groups`,
        hasSubmenu: isMobile,
        groups: isMobile ? userGroups : undefined,
      });
    }

    if (base && showSvgRuntime) {
      items.push({
        text: d.svgRuntime,
        icon: <DnsIcon />,
        path: `${base}/svg-runtime`
      });
    }

    return items;
  };

  // Функция для рендера drawer с функционалом страницы для мобильных
  const drawer = (
    <Box sx={{ padding: 0, overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Профиль пользователя наверху как оглавление */}
      {authState.result && (
        <Box sx={{ 
          p: 2, 
          backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#f5f5f5' : '#1a237e') : '#1a237e', 
          color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff', 
          borderBottom: `1px solid ${isMobile ? themeColors.borderColor : '#263238'}` 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <AccountCircleIcon sx={{ 
              fontSize: 36, 
              mr: 2, 
              color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff' 
            }} />
            <Box>
              <Typography variant="subtitle1" sx={{ 
                color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff', 
                fontWeight: 'bold', 
                fontSize: '1rem' 
              }}>
                {authState.userName || d.userFallback}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: (isMobile || authState.role === 'user') ? themeColors.textSecondary : '#90a4ae', 
                fontSize: '0.8rem' 
              }}>
                {authState.role === 'superadmin' ? d.roleSuperadminLong : 
                 authState.role === 'admin' ? d.roleAdminLong : d.roleUserLong}
              </Typography>
            </Box>
            </Box>
            <PublicLocaleToggle
              lang={lang}
              onChange={setLang}
              labelUk={d.languageLabelUk}
              labelEn={d.languageLabelEn}
              tone={isLightUserTheme ? 'light' : 'dark'}
            />
          </Box>
        </Box>
      )}

      {/* Основное меню навигации */}
      {authState.result && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {mapsEnabled &&
            authState.role === 'user' &&
            getNormalizedPath().startsWith('/groups') && (
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: (isMobile || authState.role === 'user')
                      ? themeColors.textSecondary
                      : '#90a4ae',
                    fontSize: '0.72rem',
                  }}
                >
                  <MapIcon sx={{ fontSize: 16 }} />
                  {d.mapModeTitle || 'View mode'}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <ToggleButtonGroup
                    size="small"
                    value={groupsViewMode}
                    exclusive
                    fullWidth
                    onChange={(_, nextMode) => {
                      if (!nextMode) return;
                      setGroupsViewMode(nextMode);
                      writeGroupsViewMode(nextMode);
                      emitGroupsViewModeChanged(nextMode);
                    }}
                    sx={viewModeToggleSx}
                  >
                    <ToggleButton value="cards">{d.mapModeCards || 'Cards'}</ToggleButton>
                    <ToggleButton value="map">{d.mapModeMap || 'Map'}</ToggleButton>
                  </ToggleButtonGroup>
                  {groupsViewMode === 'map' && (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={groupsMapProvider}
                        onChange={(e) => {
                          const next = String(e.target.value || '');
                          setGroupsMapProvider(next);
                          writeGroupsMapProvider(next);
                          emitGroupsMapProviderChanged(next);
                        }}
                        sx={{
                          color: (isMobile || authState.role === 'user')
                            ? themeColors.textPrimary
                            : '#ffffff',
                          backgroundColor: (isMobile || authState.role === 'user')
                            ? (mobileTheme === 'white' ? '#ffffff' : 'rgba(255,255,255,0.06)')
                            : 'rgba(255,255,255,0.08)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: (isMobile || authState.role === 'user')
                              ? themeColors.borderColor
                              : 'rgba(255,255,255,0.24)',
                          },
                          '& .MuiSvgIcon-root': {
                            color: (isMobile || authState.role === 'user')
                              ? themeColors.textPrimary
                              : '#ffffff',
                          },
                        }}
                      >
                        {MAP_PROVIDER_OPTIONS.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>
            )}
          <List sx={{ pt: 1 }}>
            {getMenuItems().map((item) => (
              <React.Fragment key={item.text}>
                <ListItem disablePadding>
                  <ListItemButton
                    component={item.path ? Link : 'div'}
                    to={item.path || ''}
                    selected={location.pathname === item.path}
                    onClick={item.hasSubmenu ? () => setGroupsOpen(!groupsOpen) : () => setMobileOpen(false)}
                    sx={{
                      color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit',
                      '&:hover': {
                        backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#f0f0f0' : '#1a237e') : '#1a237e',
                      },
                      '&.Mui-selected': {
                        backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#e3f2fd' : '#1a237e') : '#1a237e',
                        color: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#1976d2' : '#ffffff') : '#ffffff',
                        '&:hover': {
                          backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#bbdefb' : '#000051') : '#000051',
                        }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: location.pathname === item.path 
                        ? ((isMobile || authState.role === 'user') && mobileTheme === 'white' ? '#1976d2' : 'primary.main')
                        : ((isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit')
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit'
                      }}
                    />
                    {item.hasSubmenu && (
                      <Box sx={{ color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit' }}>
                        {groupsOpen ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
                {item.hasSubmenu && (
                  <Collapse in={groupsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.groups?.map((group) => {
                        const base = getProjectBase();
                        const groupPath = `${base}/groups/${group.id}`;
                        return (
                        <ListItemButton 
                          key={group.id} 
                          sx={{ 
                            pl: 4,
                            color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit',
                            '&:hover': {
                              backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#f0f0f0' : '#1a237e') : '#1a237e',
                            },
                            '&.Mui-selected': {
                              backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#e3f2fd' : '#1a237e') : '#1a237e',
                              color: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#1976d2' : '#ffffff') : '#ffffff',
                              '&:hover': {
                                backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#bbdefb' : '#000051') : '#000051',
                              }
                            }
                          }}
                          component={Link}
                          to={groupPath}
                          selected={location.pathname === groupPath}
                          onClick={() => {
                            handleGroupSelect(group.id);
                            setMobileOpen(false); // Закрываем drawer при выборе группы
                          }}
                        >
                          <ListItemIcon sx={{ 
                            color: location.pathname === groupPath
                              ? ((isMobile || authState.role === 'user') && mobileTheme === 'white' ? '#1976d2' : 'inherit')
                              : ((isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit')
                          }}>
                            <GroupIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary={group.name}
                            primaryTypographyProps={{
                              color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit'
                            }}
                          />
                        </ListItemButton>
                      );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
          </List>

          {/* Контекстные действия */}
          {getContextMenuItems().length > 0 && (
            <>
              <Divider sx={{ my: 1, borderColor: (isMobile || authState.role === 'user') ? themeColors.borderColor : 'rgba(255, 255, 255, 0.12)' }} />
              <List dense sx={{ px: 1 }}>
                <Typography variant="overline" sx={{ 
                  px: 2, 
                  color: (isMobile || authState.role === 'user') ? themeColors.textSecondary : '#90a4ae', 
                  fontSize: '0.7rem' 
                }}>
                  {d.actions}
                </Typography>
                {getContextMenuItems().map((item, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton 
                      onClick={() => {
                        item.action();
                        setMobileOpen(false);
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        mx: 1,
                        color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit',
                        '&:hover': {
                          backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#f0f0f0' : '#37474f') : '#37474f',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 32,
                        color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit'
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label}
                        primaryTypographyProps={{ 
                          fontSize: '0.85rem',
                          color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      )}





      {/* Кнопка выхода внизу */}
      {authState.result && (
        <Box sx={{ 
          p: 2, 
          borderTop: (isMobile || authState.role === 'user') ? 'none' : `1px solid ${themeColors.borderColor}`, 
          backgroundColor: (isMobile || authState.role === 'user') ? themeColors.cardBackground : '#162238' 
        }}>
          <ListItemButton 
            onClick={() => {
              handleLogoutFromMenu();
              setMobileOpen(false);
            }}
            sx={{ 
              backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#1976d2' : '#1a237e') : '#1a237e',
              color: '#ffffff',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '#1565c0' : '#000051') : '#000051',
              }
            }}
          >
            <ListItemIcon sx={{ color: '#ffffff' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary={d.logout}
              primaryTypographyProps={{ color: '#ffffff' }}
            />
          </ListItemButton>
        </Box>
      )}
    </Box>
  );

  const normalizedPathForTitle = (location.pathname || '').replace(/^\/p\/[^/]+/, '');
  const isGroupsPage = normalizedPathForTitle.startsWith('/groups');
  const resolveUidName = (uidCode) => {
    if (!uidCode) return null;
    const uidConfigs = typeof window !== 'undefined' ? (window.__UID_CONFIGS__ || {}) : {};
    return (
      uidConfigs[uidCode]?.name
      || uidConfigs[String(uidCode).toLowerCase()]?.name
      || uidConfigs[String(uidCode).toUpperCase()]?.name
      || null
    );
  };

  return (
    <ThemeProvider theme={appTheme}>
      <Box sx={{ display: 'flex' }}>
        <StyledAppBar 
          themecolors={themeColors}
          userrole={authState.role}
          position="fixed" 
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            boxShadow: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 12px rgba(0, 0, 0, 0.3)') : '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          <Toolbar sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Кнопка меню только для мобильных */}
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 1, 
                  display: { xs: 'block', md: 'none' }, // Показываем только на мобильных
                  color: isMobile ? themeColors.textPrimary : 'inherit'
                }}
              >
                <MenuIcon />
              </IconButton>

              {/* Кнопки "Назад" для мобильных - рядом с меню */}
              {isMobile && authState.role === 'user' && getNormalizedPath() === '/sets' && (
                <IconButton
                  onClick={() => {
                    localStorage.removeItem('selectedSet');
                    setCurrentSetName('');
                    const base = getProjectBase();
                    navigate(`${base}/groups`);
                  }}
                  sx={{ 
                    mr: 1,
                    color: themeColors.textPrimary,
                    backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              {isMobile && authState.role === 'user' && getNormalizedPath().startsWith('/trends/') && (
                <IconButton
                  onClick={() => {
                    const normalized = getNormalizedPath().split('/');
                    const stationId = normalized[2];
                    handleBackFromAlarmOrTrend(stationId);
                  }}
                  sx={{ 
                    mr: 1,
                    color: themeColors.textPrimary,
                    backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
</IconButton>
              )}

              {isMobile &&
                authState.role === 'user' &&
                (normalizedPathStartsWithStationDetails(getNormalizedPath()) ||
                  normalizedPathStartsWithProjectCard(getNormalizedPath())) && (
                <IconButton
                  onClick={handleBackFromMoreDetails}
                  sx={{ 
                    mr: 1,
                    color: themeColors.textPrimary,
                    backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              
              {isMobile &&
                authState.role === 'user' &&
                (getNormalizedPath().startsWith('/alarms/') ||
                  getNormalizedPath().startsWith('/alarms-v2/')) && (
                <IconButton
                  onClick={() => {
                    const normalized = getNormalizedPath().split('/');
                    const stationId = normalized[2];
                    handleBackFromAlarmOrTrend(stationId);
                  }}
                  sx={{ 
                    mr: 1,
                    color: themeColors.textPrimary,
                    backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              
              {/* Убираем дублирование - мобильная навигация теперь только в drawer */}
              
              {/* Кнопки "Назад" только для десктопа */}
              {!isMobile && authState.role === 'user' && getNormalizedPath() === '/sets' && (
                <IconButton
                  onClick={() => {
                    localStorage.removeItem('selectedSet');
                    setCurrentSetName('');
                    const base = getProjectBase();
                    navigate(`${base}/groups`);
                  }}
                  sx={{ 
                    mr: 2,
                    color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              {!isMobile && authState.role === 'user' && getNormalizedPath().startsWith('/trends/') && (
                <IconButton
                  onClick={() => {
                    const normalized = getNormalizedPath().split('/');
                    const stationId = normalized[2];
                    handleBackFromAlarmOrTrend(stationId);
                  }}
                  sx={{ 
                    mr: 2,
                    color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              {!isMobile &&
                authState.role === 'user' &&
                (normalizedPathStartsWithStationDetails(getNormalizedPath()) ||
                  normalizedPathStartsWithProjectCard(getNormalizedPath())) && (
                <IconButton
                  onClick={handleBackFromMoreDetails}
                  sx={{ 
                    mr: 2,
                    color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              
              {!isMobile &&
                authState.role === 'user' &&
                (getNormalizedPath().startsWith('/alarms/') ||
                  getNormalizedPath().startsWith('/alarms-v2/')) && (
                <IconButton
                  onClick={() => {
                    const normalized = getNormalizedPath().split('/');
                    const stationId = normalized[2];
                    handleBackFromAlarmOrTrend(stationId);
                  }}
                  sx={{ 
                    mr: 2,
                    color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : 'inherit',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              <Typography variant="h6" component="div" sx={{ 
                color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff', 
                minWidth: 'fit-content',
                // Адаптивность для мобильных устройств
                fontSize: { xs: '1rem', md: '1.25rem' }, // Меньше на мобильных
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: '200px', sm: '300px', md: 'none' }, // Ограничиваем ширину на мобильных
                flex: { xs: 1, md: 'none' } // На мобильных занимаем доступное место
              }}>
                {!authState.result
                  ? d.brandScada
                  : authState.role === 'superadmin'
                    ? d.sysAdminTitle
                    : authState.role === 'user'
                      ? (normalizedPathStartsWithStationDetails(normalizedPathForTitle)
                        || normalizedPathForTitle.startsWith('/trends/')
                        || normalizedPathForTitle.startsWith('/alarms/')
                        || normalizedPathForTitle.startsWith('/alarms-v2/')
                        || normalizedPathStartsWithProjectCard(normalizedPathForTitle)
                          ? (() => {
                              if (normalizedPathStartsWithProjectCard(normalizedPathForTitle)) {
                                const projectCardTitle = getProjectCardTitleFromPath();
                                if (projectCardTitle) return projectCardTitle;
                              }
                              // Улучшенная загрузка данных станции
                              const stationData = JSON.parse(localStorage.getItem('currentStationData') || '{}');
                              const stateStationData = location.state?.sensorItemProps;
                              const stationId = normalizedPathForTitle.split('/')[2];
                              const qs = new URLSearchParams(location.search || '');
                              const isProjectOnly =
                                qs.get('projectOnly') === '1' ||
                                qs.get('projectOnly') === 'true';
                              const queryStation = qs.get('station') || '';
                              const queryUid = qs.get('uid') || '';
                              const queryUuid = stationId
                                ? getStationUuidFromSession(projectSlug, Number(stationId), getStationTypeValue(stateStationData || stationData))
                                : null;
                              
                              // Приоритет: данные из state, затем из localStorage
                              let stationName =
                                stateStationData?.Station_name ||
                                stateStationData?.name ||
                                stateStationData?.station_name ||
                                stationData?.Station_name ||
                                stationData?.name ||
                                stationData?.station_name ||
                                resolvedHeaderStationName;
                              const uidCode =
                                stateStationData?.uid ||
                                stateStationData?.uid_code ||
                                stationData?.uid ||
                                stationData?.uid_code ||
                                queryUid ||
                                queryUuid;
                              const uidName = resolveUidName(uidCode);
                              if ((!stationName || isGenericStationTitle(stationName)) && uidName) {
                                stationName = uidName;
                              }
                              
                              // Добавляем префикс в зависимости от страницы
                              let prefix = '';
                              if (normalizedPathForTitle.startsWith('/trends/')) {
                                prefix = ' ';
                              } else if (
                                normalizedPathForTitle.startsWith('/alarms/') ||
                                normalizedPathForTitle.startsWith('/alarms-v2/')
                              ) {
                                prefix = ' ';
                                if (isProjectOnly) {
                                  return `${prefix}${d.projectAllEvents}`;
                                }
                              }

                              if (
                                (normalizedPathForTitle.startsWith('/alarms/') ||
                                  normalizedPathForTitle.startsWith('/alarms-v2/')) &&
                                (!stationName || isGenericStationTitle(stationName)) &&
                                (queryStation || stationId) &&
                                uidName
                              ) {
                                stationName = d.stationWithUid(String(queryStation || stationId), uidName);
                              }
                              
                              // Если данных нет, показываем нейтральный заголовок станции без "ID"
                              if (!stationName) {
                                const fallbackStationNum = queryStation || stationId;
                                return fallbackStationNum
                                  ? `${prefix}${d.stationOnly(fallbackStationNum)}`
                                  : d.stationLoading;
                              }
                              
                              return `${prefix}${stationName}`;
                            })()
                          : (currentSetName || currentGroupName || d.brandScada))
                      : d.brandScada}
              </Typography>
            </Box>

            {/* Табы только для десктопа */}
            {!isMobile && authState.result && authState.role === 'superadmin' && (
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ 
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#ffffff',
                    },
                    '& .MuiTab-root': {
                      color: '#90a4ae',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      minHeight: '48px',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        color: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        color: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      }
                    }
                  }}
                >
                  {getSuperAdminTabs().map((tab) => (
                    <Tab key={tab.value} label={tab.label} />
                  ))}
                </Tabs>
                
                {/* Кнопка возврата к настройкам UID/Проектов */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={() => {
                    navigate('/v2/superadmin');
                  }}
                  sx={{
                    ml: 2,
                    color: location.pathname === '/v2/superadmin' ? '#ffffff' : '#90a4ae',
                    borderColor: location.pathname === '/v2/superadmin' ? '#ffffff' : '#90a4ae',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    minHeight: '36px',
                    px: 2,
                    transition: 'all 0.2s ease-in-out',
                    bgcolor: location.pathname === '/v2/superadmin' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    '&:hover': {
                      color: '#ffffff',
                      borderColor: '#ffffff',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {d.uidProjectSettings}
                </Button>
              </Box>
            )}

            {!isMobile && authState.result && authState.role === 'admin' && (
              <Box sx={{ display: 'flex', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ 
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#ffffff',
                    },
                    '& .MuiTab-root': {
                      color: '#90a4ae',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      minHeight: '48px',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        color: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        color: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      }
                    }
                  }}
                >
                  {getAdminTabs().map((tab) => (
                    <Tab key={tab.value} label={tab.label} />
                  ))}
                </Tabs>
              </Box>
            )}

            {/* Группы и переключатель режимов только для десктопа */}
            {!isMobile && authState.result && authState.role === 'user' && (
              <Box sx={{ display: 'flex', position: 'absolute', left: '50%', transform: 'translateX(-50%)', alignItems: 'center', gap: 2 }}>
                {userGroups.length > 0 && (
                  <Button
                    onClick={handleGroupMenuOpen}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      color: (isMobile || authState.role === 'user') ? themeColors.textSecondary : '#90a4ae',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      minHeight: '48px',
                      transition: 'all 0.2s ease-in-out',
                      px: 3,
                      '&:hover': {
                        color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    {d.myGroups}
                  </Button>
                )}


                {userGroups.length > 0 && (
                  <Menu
                    anchorEl={groupMenuAnchor}
                    open={Boolean(groupMenuAnchor)}
                    onClose={handleGroupMenuClose}
                    PaperProps={{
                      sx: {
                        bgcolor: (isMobile || authState.role === 'user') ? themeColors.cardBackground : '#162238',
                        border: (isMobile || authState.role === 'user') ? `1px solid ${themeColors.borderColor}` : '1px solid #263238',
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff',
                          '&:hover': {
                            backgroundColor: (isMobile || authState.role === 'user') ? themeColors.hoverBackground : '#1a237e',
                          }
                        }
                      }
                    }}
                  >
                    {userGroups.map((group) => (
                      <MenuItem 
                        key={group.id} 
                        onClick={() => handleGroupSelect(group.id)}
                        sx={{ minWidth: 200 }}
                      >
                        <GroupIcon sx={{ 
                          mr: 1,
                          color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff'
                        }} />
                        {group.name}
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </Box>
            )}

            {authState.result && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {mapsEnabled && authState.role === 'user' && isGroupsPage && (
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                    {groupsViewMode === 'map' && (
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <Select
                          value={groupsMapProvider}
                          onChange={(e) => {
                            const next = String(e.target.value || '');
                            setGroupsMapProvider(next);
                            writeGroupsMapProvider(next);
                            emitGroupsMapProviderChanged(next);
                          }}
                          sx={{
                            color: themeColors.textPrimary,
                            backgroundColor: isLightUserTheme ? '#ffffff' : 'rgba(255,255,255,0.08)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: isLightUserTheme
                                ? 'rgba(15, 23, 42, 0.28)'
                                : 'rgba(255,255,255,0.24)',
                            },
                            '& .MuiSvgIcon-root': { color: themeColors.textPrimary },
                          }}
                        >
                          {MAP_PROVIDER_OPTIONS.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <ToggleButtonGroup
                      size="small"
                      value={groupsViewMode}
                      exclusive
                      onChange={(_, nextMode) => {
                        if (!nextMode) return;
                        setGroupsViewMode(nextMode);
                        writeGroupsViewMode(nextMode);
                        emitGroupsViewModeChanged(nextMode);
                      }}
                      sx={viewModeToggleSx}
                    >
                      <ToggleButton value="cards">{d.mapModeCards || 'Cards'}</ToggleButton>
                      <ToggleButton value="map">{d.mapModeMap || 'Map'}</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
                {!isMobile && (
                  <PublicLocaleToggle
                    lang={lang}
                    onChange={setLang}
                    labelUk={d.languageLabelUk}
                    labelEn={d.languageLabelEn}
                    tone={isLightUserTheme ? 'light' : 'dark'}
                  />
                )}
                {/* Выпадающее меню проектов для суперадмина */}
                {authState.role === 'superadmin' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl sx={{ minWidth: 200 }}>
                      <Select
                        value={currentProjectSlug}
                        onChange={handleProjectChange}
                        displayEmpty
                        sx={{ 
                          color: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.23)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#ffffff',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#ffffff',
                          }
                        }}
                      >
                        {superAdminProjects.length > 0 ? (
                          superAdminProjects.map(project => (
                            <MenuItem key={project.slug} value={project.slug}>
                              {project.name} ({project.slug})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            {d.noProjects}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={loadSuperAdminProjects}
                      sx={{ 
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                      title={d.refreshProjectList}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                )}

                {/* Кнопка переключения темы для мобильных и пользователей */}
                {(isMobile || authState.role === 'user') && (
                  <IconButton
                    onClick={toggleMobileTheme}
                    sx={{
                      color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      }
                    }}
                  >
                    {mobileTheme === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                )}
                
                {/* Профиль только для десктопа - на мобильных он в drawer */}
                <Button
                  onClick={handleUserMenuOpen}
                  endIcon={<ArrowDropDownIcon />}
                  startIcon={<AccountCircleIcon />}
                  sx={{
                    color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    minHeight: '48px',
                    transition: 'all 0.2s ease-in-out',
                    px: 2,
                    borderRadius: '8px',
                    display: { xs: 'none', md: 'flex' }, // Скрываем на мобильных
                    '&:hover': {
                      backgroundColor: (isMobile || authState.role === 'user') ? (mobileTheme === 'white' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)') : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {authState.userName || d.userFallback}
                </Button>
                
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    sx: {
                      bgcolor: (isMobile || authState.role === 'user') ? themeColors.cardBackground : '#162238',
                      border: (isMobile || authState.role === 'user') ? `1px solid ${themeColors.borderColor}` : '1px solid #263238',
                      mt: 1,
                      minWidth: 200,
                      '& .MuiMenuItem-root': {
                        color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff',
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: (isMobile || authState.role === 'user') ? themeColors.hoverBackground : '#1a237e',
                        }
                      }
                    }
                  }}
                >
                  {getContextMenuItems().map((item) => (
                    <MenuItem 
                      key={item.label}
                      onClick={item.action}
                    >
                      <ListItemIcon sx={{ 
                        color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff' 
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.label} />
                    </MenuItem>
                  ))}
                  <Divider sx={{ 
                    my: 1, 
                    bgcolor: (isMobile || authState.role === 'user') ? themeColors.borderColor : '#334155' 
                  }} />
                  <MenuItem onClick={handleLogoutFromMenu}>
                    <LogoutIcon sx={{ 
                      mr: 2, 
                      fontSize: '20px',
                      color: (isMobile || authState.role === 'user') ? themeColors.textPrimary : '#ffffff'
                    }} />
                    {d.logout}
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Toolbar>
        </StyledAppBar>
        
        <Box component="nav">
          {/* Мобильный drawer с полным функционалом */}
          <StyledDrawer
            themecolors={themeColors}
            userrole={authState.role}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ 
              display: { xs: 'block', md: 'none' },
              // Учитываем высоту AppBar - drawer начинается под ним
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: 280,
                padding: 0,
                top: '64px', // Отступ сверху равный высоте AppBar
                height: 'calc(100% - 64px)', // Высота минус высота AppBar
              }
            }}
          >
            {drawer}
          </StyledDrawer>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default DrawerAppBar;
